import { FC, useEffect, useMemo } from 'react';
import moment from 'moment';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Box, Paper, Grid, Button, TextField, CircularProgress, Autocomplete, Typography } from '@mui/material';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

import { useWorkplacesContext } from 'module/workplaces/context/WorkplacesContext';
import { useToastContext } from 'core/components/Toast/context/ToastContext';

import { PostShifts, EditShifts } from 'core/API/shifts';
import { getJobCodes } from 'core/API/scheduler';
import { IShiftPayload } from 'module/schedule/interfaces/ShiftsInterface';
import { UserAvailability } from 'core/interfaces/userInterface';

import { ICellClick } from 'module/schedule/pages/SchedulePage';

import unavailableIcon from 'assets/images/unavailable.png';
import minorIcon from 'assets/images/minor.png';
import overtimeIcon from 'assets/images/overtime.png';

// import ConfirmDialog from '../../../schedule/Modals/ConfirmDialog/ConfirmDialog';
import styles from 'module/events/pages/CreateEvent.module.scss';

import './AddShiftPage.scss';
import { Days } from '../../../../types';

// Extended interface to include job code and employee from resource clicks
export interface IExtendedCellClick extends ICellClick {
	jobCodeFromResource?: {
		id: string;
		'@id': string;
		label: string;
		jobNumber?: string;
		backgroundColor?: string;
		textColor?: string;
	};
	employeeFromResource?: {
		id: string;
		'@id': string;
		firstName: string;
		lastName: string;
		fullName: string;
	};
}

// Validation schema for the shift form
const getValidationSchema = (minShiftLength?: number, maxShiftLength?: number) => {
	const minHours = minShiftLength || 2;
	const maxHours = maxShiftLength || 16;
	
	return Yup.object({
		startAt: Yup.string().required('Start date and time are required'),
		endAt: Yup.string()
			.required('End date and time are required')
			.test(
				'end-after-start',
				'End date and time must be after start date and time',
				function (value) {
					const { startAt } = this.parent;
					if (!startAt || !value) return true; // Let required validation handle empty values

					const startMoment = moment(startAt);
					const endMoment = moment(value);

					return endMoment.isAfter(startMoment);
				}
			)
			.test(
				'min-shift-length',
				`Shift length must be at least ${minHours} hours`,
				function (value) {
					const { startAt } = this.parent;
					if (!startAt || !value) return true;

					const startMoment = moment(startAt);
					const endMoment = moment(value);
					const durationHours = endMoment.diff(startMoment, 'hours', true);

					return durationHours >= minHours;
				}
			)
			.test(
				'max-shift-length',
				`Shift length cannot exceed ${maxHours} hours`,
				function (value) {
					const { startAt } = this.parent;
					if (!startAt || !value) return true;

					const startMoment = moment(startAt);
					const endMoment = moment(value);
					const durationHours = endMoment.diff(startMoment, 'hours', true);

					return durationHours <= maxHours;
				}
			),
		jobCode: Yup.string().nullable().required('Job code is required'),
		// employee is optional (null allowed for open shifts)
		employee: Yup.string().nullable(),
		workplace: Yup.string().required('Workplace is required'),
	});
};

// Use the interface from ShiftsInterface for consistency
type IShiftForm = IShiftPayload;

interface IAddShiftPage {
	onBack: () => void;
	editShift?: any;
	addNewShift?: IExtendedCellClick;
	generalSettings?: { 
		defaultShiftLengthHours: number;
		minShiftLength?: number;
		maxShiftLength?: number;
		ageOfMinor?: number;
		overtimeDailyHoursAt?: number;
		overtimeWeeklyHoursAt?: number;
	} | null;
	workplaceUsers?: any[]; // Users data from SchedulePage for reference
	daysData?: Days[]; // Days data from SchedulePage for availability checking
	weekStartDate?: Date; // Start of the selected business week
	workplaceWorkingHoursStart?: string; // Workplace working hours start time (HH:mm format)
}

const AddShiftPage: FC<IAddShiftPage> = ({ onBack, editShift, addNewShift, generalSettings, workplaceUsers: workplaceUsersFromParent, daysData, weekStartDate, workplaceWorkingHoursStart }) => {
	const { workplaceId, timezones: _timezones } = useWorkplacesContext();
	const { showToast } = useToastContext();
	const queryClient = useQueryClient();

	// const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);

	// Use workplace users from parent (already filtered by SchedulePage)
	const employees = useMemo(() => workplaceUsersFromParent || [], [workplaceUsersFromParent]);

	// console.log('AddShiftPage workplaceUsersFromParent:', workplaceUsersFromParent);
	// console.log('daysData:', daysData);
	// Helper function to calculate employee status
	const getEmployeeStatus = (employee: any, shiftStartTime?: Date, shiftEndTime?: Date) => {
		const statuses = {
			isUnavailable: false,
			isMinor: false,
			hasOvertime: false,
			availabilityMessage: null as string | null,
		};

		if (!employee || !generalSettings) return statuses;

		// Check if employee is a minor
		const minorAge = generalSettings?.ageOfMinor || 18;
		if (employee.dateOfBirth) {
			const age = moment().diff(moment(employee.dateOfBirth), 'years');
			statuses.isMinor = age < minorAge;
		}

		// Check for availability conflicts or time off
		if (shiftStartTime && shiftEndTime) {
			const shiftMoment = moment(shiftStartTime);
			const shiftDayOfWeek = shiftMoment.day(); // 0 = Sunday, 1 = Monday, etc.
			const shiftStartTimeOnly = shiftMoment.format('HH:mm:ss');
			const shiftEndTimeOnly = moment(shiftEndTime).format('HH:mm:ss');
			const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
			const shiftDayName = dayNames[shiftDayOfWeek];
			
			// Check userAvailability (recurring weekly pattern)
			if (daysData) {
				// Check if employee has ANY availability set for any day
				// If they have some availability set, then days without availability are considered unavailable
				// If they have NO availability set at all, they are available all days
				const hasAnyAvailability = employee.userAvailability && employee.userAvailability.length > 0;

				if (hasAnyAvailability) {
					// Find availability for this day of week
					const dayAvailabilities: UserAvailability[] = employee.userAvailability.filter((avail: any) => {
						if (!avail.day) return false;
						// Get day ID from the day reference
						const dayId = typeof avail.day === 'string' ? avail.day : avail.day['@id'];
						const dayData = daysData?.find((d: any) => d['@id'] === dayId || `/api/days/${d.id}` === dayId);
						// console.log('Matching day ID:', dayId, dayData);
						// Match day label with shift day name
						return dayData && dayData.label === shiftDayName;
					});
					// console.log('Day availabilities for employee', employee.id, dayAvailabilities);

					if (dayAvailabilities.length > 0) {
						// Get the latest availability for this day (by startAt timestamp)
						const latestAvailability = dayAvailabilities.reduce((latest: UserAvailability | null, current: UserAvailability) => {
							if (!latest) return current;
							const latestStartAt = moment(latest.startAt);
							const currentStartAt = moment(current.startAt);
							return currentStartAt.isAfter(latestStartAt) ? current : latest;
						}, null);

						if (latestAvailability && latestAvailability.startAt && latestAvailability.endAt) {
							// Check if startAt and endAt are the same (unavailable for entire day)
							const availStartTimeOnly = moment(latestAvailability.startAt).format('HH:mm:ss');
							const availEndTimeOnly = moment(latestAvailability.endAt).format('HH:mm:ss');
							
							if (availStartTimeOnly === availEndTimeOnly) {
								// Person is not available for the entire day
								statuses.isUnavailable = true;
								statuses.availabilityMessage = `Not available on ${shiftDayName}`;
							} else {
								// Check if shift time falls within the availability window
								const isWithin = shiftStartTimeOnly >= availStartTimeOnly && shiftEndTimeOnly <= availEndTimeOnly;

								if (!isWithin) {
									statuses.isUnavailable = true;
									statuses.availabilityMessage = `Available on ${shiftDayName}: ${moment(latestAvailability.startAt).format('h:mm A')} - ${moment(latestAvailability.endAt).format('h:mm A')}`;
								}
							}
						}
					} else {
						// Employee has availability set for other days but not for this day
						// This means they are unavailable on this day
						statuses.isUnavailable = true;
						statuses.availabilityMessage = `Not available on ${shiftDayName}`;
					}
				}
				// If hasAnyAvailability is false, employee has no availability set at all,
				// so they are available all days (no issue)
			}
			// console.log('Employee userTimeOff:', employee.userTimeOff);

			// Check userTimeOff (specific date ranges)
			if (employee.userTimeOff && employee.userTimeOff.length > 0) {
				// Extract only the date part of the shift (ignore time)
				const shiftDate = shiftMoment.format('YYYY-MM-DD');
				
				const matchingTimeOff = employee.userTimeOff.find((timeOff: any) => {
					if (timeOff.startAt && timeOff.endAt) {
						// Extract only date parts for comparison (ignore time)
						const timeOffStartDate = moment(timeOff.startAt).format('YYYY-MM-DD');
						const timeOffEndDate = moment(timeOff.endAt).format('YYYY-MM-DD');
						console.log('shiftDate vs timeOff:', shiftDate, timeOffStartDate, timeOffEndDate);
						// Check if shift date falls within time-off period (inclusive)
						return shiftDate >= timeOffStartDate && shiftDate <= timeOffEndDate;
					}
					return false;
				});
				
				if (matchingTimeOff) {
					statuses.isUnavailable = true;
					// Format time-off dates for display
					const timeOffStart = moment(matchingTimeOff.startAt).format('MMM D, YYYY');
					const timeOffEnd = moment(matchingTimeOff.endAt).format('MMM D, YYYY');
					statuses.availabilityMessage = timeOffStart === timeOffEnd 
						? `Employee has time off on ${timeOffStart}`
						: `Employee has time off: ${timeOffStart} - ${timeOffEnd}`;
				}
			}
		}

		return statuses;
	};

	// Fetch job codes
	const { data: jobCodes, isLoading: isLoadingJobCodes } = useQuery(
		['jobCodes'],
		() => getJobCodes(),
		{
			staleTime: 300000,
		}
	);

	// Sort job codes by job number
	const sortedJobCodes = useMemo(() => {
		if (!jobCodes) return [];
		return [...jobCodes].sort((a, b) => {
			const aNum = a.jobNumber ?? 999;
			const bNum = b.jobNumber ?? 999;
			return aNum - bNum;
		});
	}, [jobCodes]);

	// Sort employees by name and filter out @byod.ai users
	const sortedEmployees = useMemo(() => {
		if (!employees) return [];
		// Filter out users with @byod.ai emails
		const filteredEmployees = employees.filter((emp) => {
			const email = emp.email?.toLowerCase() || '';
			return !email.endsWith('@byod.ai');
		});
		return [...filteredEmployees].sort((a, b) => {
			const aName = `${a.firstName} ${a.lastName}`;
			const bName = `${b.firstName} ${b.lastName}`;
			return aName.localeCompare(bName);
		});
	}, [employees]);

	// Use editShift data directly instead of fetching from API
	// No need for separate fetchedShift variable since we use editShift directly
	// timeSlots/baseDates not required in this version (kept for future enhancements)
	const { mutate: addShift, isLoading: isAddLoading } = useMutation(PostShifts, {
		onSuccess: () => {
			showToast('success', 'Shift successfully added!');
			queryClient.invalidateQueries(['shifts']);
			onBack();
		},
		onError: (error: any) => {
			showToast('error', error?.message || 'Failed to add shift');
		},
	});

	const { mutate: updateShift, isLoading: isUpdateLoading } = useMutation(
		(params: { id: number; body: IShiftPayload }) => EditShifts(params.id, params.body),
		{
			onSuccess: () => {
				showToast('success', 'Shift successfully updated!');
				queryClient.invalidateQueries(['shifts']);
				onBack();
			},
			onError: (error: any) => {
				showToast('error', error?.message || 'Failed to update shift');
			},
		}
	);

	const initialValues = useMemo(() => {
		// Store dates in local timezone for UI display
		// For current week, default to today's date; for past/future weeks, use week start
		let defaultStartDate: Date;
		if (weekStartDate) {
			const today = moment().startOf('day');
			const weekStart = moment(weekStartDate).startOf('day');
			const weekEnd = moment(weekStartDate).add(6, 'days').endOf('day');
			
			// Check if today falls within the week range
			const isCurrentWeek = today.isBetween(weekStart, weekEnd, null, '[]');
			
			if (isCurrentWeek) {
				// Current week: use today's date
				defaultStartDate = moment().startOf('day').toDate();
			} else {
				// Past or future week: use week start
				defaultStartDate = moment(weekStartDate).startOf('day').toDate();
			}
		} else {
			defaultStartDate = moment().startOf('day').toDate();
		}
		
		// Apply workplace working hours start time to the default start date
		if (workplaceWorkingHoursStart && !editShift && !addNewShift?.start_at) {
			const [hours, minutes] = workplaceWorkingHoursStart.split(':').map(Number);
			defaultStartDate = moment(defaultStartDate).set({ hour: hours, minute: minutes, second: 0, millisecond: 0 }).toDate();
		}
		
		const startTime = editShift?.start || addNewShift?.start_at || defaultStartDate;
		const defaultHours = generalSettings?.defaultShiftLengthHours || 8;
		let endTime: Date;
		if (editShift?.end) {
			endTime = new Date(editShift.end);
		} else if (addNewShift?.end_at) {
			endTime = new Date(addNewShift.end_at);
		} else {
			// For new shifts, calculate end time using default shift length
			endTime = moment(startTime).add(defaultHours, 'hours').toDate();
		}

		return {
			workplace: `api/workplaces/${workplaceId}`,
			startAt: typeof startTime === 'string' ? new Date(startTime).toISOString() : startTime.toISOString(),
			endAt: endTime.toISOString(),
			employee: (() => {
				// Priority: editShift employee, then employee from resource click, then null
				if (editShift?.employee && editShift.employee['@id']) {
					return editShift.employee['@id'];
				}
				if (addNewShift?.employeeFromResource && addNewShift.employeeFromResource['@id']) {
					return addNewShift.employeeFromResource['@id'];
				}
				return null;
			})(),
			jobCode: (() => {
				// Priority: editShift jobCode, then jobCode from resource click, then null
				if (editShift?.jobCode && editShift.jobCode['@id']) {
					return editShift.jobCode['@id'];
				}
				if (addNewShift?.jobCodeFromResource && addNewShift.jobCodeFromResource['@id']) {
					return addNewShift.jobCodeFromResource['@id'];
				}
				return null;
			})(),
		};
	}, [workplaceId, editShift, addNewShift, generalSettings?.defaultShiftLengthHours, weekStartDate]);

	const { values, errors, touched, submitForm, setFieldValue, handleBlur } = useFormik<IShiftForm>({
		validationSchema: getValidationSchema(
			generalSettings?.minShiftLength,
			generalSettings?.maxShiftLength
		),
		enableReinitialize: false,
		initialValues,
		onSubmit: (data: IShiftPayload) => {
			console.log('Submitting shift data:', data);
			// Send dates as-is without any timezone conversion
			const apiPayload: IShiftPayload = {
				workplace: `api/workplaces/${workplaceId}`,
				startAt: moment(data.startAt).format('YYYY-MM-DDTHH:mm:ss'),
				endAt: moment(data.endAt).format('YYYY-MM-DDTHH:mm:ss'),
				employee: data.employee || null,
				jobCode: data.jobCode || null,
				startOfWeek: weekStartDate ? moment(weekStartDate).format('YYYY-MM-DD') : undefined,
			};
			
			if (editShift?.id || editShift?.shift_id) {
				// Use shift_id from calendar event or id from editShift
				const id = editShift.shift_id || editShift.id;
				updateShift({ id, body: apiPayload });
			} else {
				addShift(apiPayload);
			}
		},
	});

	// Update end time when start time changes to maintain default shift length
	useEffect(() => {
		if (values.startAt && !editShift) {
			// Only auto-update for new shifts, not when editing existing shifts
			const defaultHours = generalSettings?.defaultShiftLengthHours || 8;
			const newEndTime = moment(values.startAt).add(defaultHours, 'hours').toISOString();
			setFieldValue('endAt', newEndTime);
		}
	}, [values.startAt, editShift, generalSettings?.defaultShiftLengthHours, setFieldValue]);

	const handleSubmitForm = (e: any) => {
		e.preventDefault();
		submitForm();
	};

	const handleDateTimeChange = (field: 'startAt' | 'endAt', date: string, time: string) => {
		// Create a proper datetime string in browser's local timezone
		const dateTimeString = `${date} ${time}:00`;
		const momentDateTime = moment(dateTimeString, 'YYYY-MM-DD HH:mm:ss');

		// Store as ISO string (represents local time)
		const isoString = momentDateTime.toISOString();
		setFieldValue(field, isoString);
	};

	const handleTimeChange = (field: 'startAt' | 'endAt', timeValue: any) => {
		if (!timeValue) return;
		
		// Get the date from current field value
		const currentDate = getDateFromISO(field === 'startAt' ? values.startAt : values.endAt);
		
		// Extract hours and minutes from dayjs object
		const hours = timeValue.hour();
		const minutes = timeValue.minute();
		const timeString = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
		
		// Update using the existing handler
		handleDateTimeChange(field, currentDate, timeString);
	};

	const getDateFromISO = (isoString: string) => {
		try {
			if (!isoString) return moment().format('YYYY-MM-DD');
			return moment(isoString).format('YYYY-MM-DD');
		} catch (e) {
			console.warn('Error parsing date from ISO:', isoString, e);
			return moment().format('YYYY-MM-DD');
		}
	};

	const getTimeFromISO = (isoString: string) => {
		try {
			if (!isoString) return moment().format('HH:mm');
			return moment(isoString).format('HH:mm');
		} catch (e) {
			console.warn('Error parsing time from ISO:', isoString, e);
			return moment().format('HH:mm');
		}
	};

	// Calculate min and max dates for start date based on business week
	const getStartDateLimits = useMemo(() => {
		if (!weekStartDate) {
			return { minDate: undefined, maxDate: undefined };
		}
		
		const today = moment().startOf('day');
		const weekStart = moment(weekStartDate).startOf('day');
		const weekEnd = moment(weekStartDate).add(6, 'days').endOf('day');
		
		// Min date is the later of today or week start (prevents past dates)
		const minDate = moment.max(today, weekStart);
		
		return {
			minDate: minDate.format('YYYY-MM-DD'),
			maxDate: weekEnd.format('YYYY-MM-DD'),
		};
	}, [weekStartDate]);

	// Constant for house shift option
	const HOUSE_SHIFT_OPTION = useMemo(
		() => ({ id: 'HOUSE_SHIFT', firstName: 'House', lastName: 'Shift', '@id': 'null' }),
		[]
	);

	// Build employee options list (includes house shift and sorted employees)
	const employeeOptions = useMemo(
		() => [HOUSE_SHIFT_OPTION, ...sortedEmployees],
		[HOUSE_SHIFT_OPTION, sortedEmployees]
	);

	// Get current employee value for the Autocomplete
	const currentEmployeeValue = useMemo(() => {
		if (values.employee === null || values.employee === undefined) {
			return HOUSE_SHIFT_OPTION;
		}
		// Try to find in employees list
		const found = employees?.find((e) => e['@id'] === values.employee);
		if (found) return found;
		// If not found but has value, return placeholder object so Autocomplete doesn't break
		return { '@id': values.employee, firstName: 'Selected', lastName: 'Employee', id: 'temp' };
	}, [values.employee, employees, HOUSE_SHIFT_OPTION]);

	if (isLoadingJobCodes) {
		return (
			<Box
				sx={{
					backgroundColor: '#f4f6fa',
					minHeight: '100vh',
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
				}}
			>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Paper sx={{ minHeight: '100vh', py: 3, borderRadius: 3, boxShadow: 2 }}>
			<Box maxWidth="1100px" mx="auto" px={3}>
				<Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
					<Button
						onClick={onBack}
						sx={{
							backgroundColor: 'rgb(68, 99, 136)',
							color: '#fff',
							textTransform: 'none',
							fontWeight: 500,
							px: 3,
							py: 0.75,
							'&:hover': { backgroundColor: 'rgb(57, 80, 111)' },
						}}
					>
						Back
					</Button>

					<Typography variant="h5" sx={{ fontWeight: 600 }}>
						{editShift ? 'Edit Shift' : 'Create Shift'}
					</Typography>

					<Button
						type="submit"
						variant="contained"
						form="addShiftForm"
						sx={{
							backgroundColor: 'rgb(68, 99, 136)',
							color: '#fff',
							px: 4,
							textTransform: 'none',
							fontWeight: 500,
							'&:hover': { backgroundColor: 'rgb(57, 80, 111)' },
						}}
						disabled={isAddLoading || isUpdateLoading}
					>
						{isAddLoading || isUpdateLoading ? 'Saving...' : 'Save'}
					</Button>
				</Box>

				<Box sx={{ p: 4 }}>
					<Typography variant="h6" gutterBottom>
						Shift Information
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
						Enter shift details and scheduling information
					</Typography>

					<form id="addShiftForm" onSubmit={handleSubmitForm}>
						<Grid container spacing={2}>
							{/* Employee */}
							<Grid item xs={12} sm={6}>
								<label className={styles.formLabel}>Employee</label>
								<Autocomplete
									options={employeeOptions}
									getOptionLabel={(option) => 
										option.id === 'HOUSE_SHIFT' ? 'House Shift' : `${option.firstName} ${option.lastName}`
									}
									value={currentEmployeeValue}
									onChange={(_, value) => {
										console.log('Selected employee:', value);
										// If HOUSE_SHIFT is selected, set employee to null; otherwise use @id
										if (value?.['@id'] === 'null') {
											setFieldValue('employee', null);
										} else {
											setFieldValue('employee', value?.['@id'] || null);
										}
									}}
									isOptionEqualToValue={(option, value) => {
										if (!option || !value) return false;
										// Both are house shift
										if (option['@id'] === 'null' && value['@id'] === 'null') return true;
										// Both are employees - compare by @id
										if (option['@id'] && value['@id'] && option['@id'] !== 'null') return option['@id'] === value['@id'];
										return false;
									}}
									fullWidth
									renderOption={(props, option) => {
								const { key: _key, ...otherProps } = props;
									if (option.id === 'HOUSE_SHIFT') {
										return (
											<li key={option['@id'] || option.id} {...otherProps}>
												House Shift
											</li>
										);
									}
									
									// Calculate employee status
									const shiftStartTime = values.startAt ? new Date(values.startAt) : undefined;
									const shiftEndTime = values.endAt ? new Date(values.endAt) : undefined;
									const status = getEmployeeStatus(option, shiftStartTime, shiftEndTime);
								
									return (
										<li key={option['@id'] || option.id} {...otherProps}>
												<Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 1 }}>
													<Box sx={{ flex: 1 }}>
														<Typography variant="body1">
															{option.firstName} {option.lastName}
														</Typography>
														{status.availabilityMessage && (
															<Typography variant="caption" sx={{ color: 'warning.main', display: 'block' }}>
																{status.availabilityMessage}
															</Typography>
														)}
													</Box>
													<Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
														{status.isUnavailable && (
															<img
																src={unavailableIcon}
																alt="Unavailable"
																title="Employee is unavailable or has time off"
																style={{ width: '18px', height: '18px', objectFit: 'contain' }}
															/>
														)}
														{status.isMinor && (
															<img
																src={minorIcon}
																alt="Minor"
																title="Employee is a minor"
																style={{ width: '18px', height: '18px', objectFit: 'contain' }}
															/>
														)}
														{status.hasOvertime && (
															<img
																src={overtimeIcon}
																alt="Overtime"
																title="Employee is in or approaching overtime"
																style={{ width: '18px', height: '18px', objectFit: 'contain' }}
															/>
														)}
													</Box>
												</Box>
											</li>
										);
									}}
									renderInput={(params) => (
										<TextField
											{...params}
											className={styles.formInput}
										/>
									)}
								/>
							</Grid>

							{/* Job Code */}
							<Grid item xs={12} sm={6}>
								<label className={styles.formLabel}>
									Job Code <span className={styles.required}>*</span>
								</label>
								<Autocomplete
									options={sortedJobCodes}
									getOptionLabel={(option) =>
										option.jobNumber
											? `${option.jobNumber} - ${option.label}`
											: option.label
									}
									value={
										values.jobCode
											? (sortedJobCodes?.find((jc) => jc['@id'] === values.jobCode) || null)
											: null
									}
									onChange={(_, value) => {
										setFieldValue('jobCode', value?.['@id'] || null);
									}}
									isOptionEqualToValue={(option, value) =>
										option['@id'] === value?.['@id']
									}
								fullWidth
								renderOption={(props, option) => {
								const { key: _key, ...otherProps } = props;
									return (
										<li key={option['@id'] || option.id} {...otherProps}>
											{option.jobNumber
												? `${option.jobNumber} - ${option.label}`
												: option.label}
										</li>
									);
								}}
								renderInput={(params) => (
										<TextField
											{...params}
											className={styles.formInput}
											error={touched.jobCode && Boolean(errors.jobCode)}
											helperText={touched.jobCode && errors.jobCode}
										/>
									)}
								/>
							</Grid>

							{/* Start Date */}
							<Grid item xs={12} sm={6}>
									<label className={styles.formLabel}>
										Start Date <span className={styles.required}>*</span>
									</label>
									<TextField
										name="startDate"
										type="date"
										className={styles.formInput}
										required
										fullWidth
										value={getDateFromISO(values.startAt)}
										onChange={(e) =>
											handleDateTimeChange(
												'startAt',
												e.target.value,
												getTimeFromISO(values.startAt)
											)
										}
										inputProps={{
											min: getStartDateLimits.minDate,
											max: getStartDateLimits.maxDate,
										}}
										// onBlur={handleBlur}
									/>
								</Grid>

								{/* Start Time */}
								<Grid item xs={12} sm={6}>
									<label className={styles.formLabel}>
										Start Time <span className={styles.required}>*</span>
									</label>
									<LocalizationProvider dateAdapter={AdapterDayjs}>
										<TimePicker
											value={dayjs(values.startAt).isValid() ? dayjs(values.startAt) : null}
											onChange={(newTime) => handleTimeChange('startAt', newTime)}
											views={['hours', 'minutes']}
											slotProps={{
												textField: {
													fullWidth: true,
													className: styles.formInput,
													error: touched.startAt && Boolean(errors.startAt),
												},
												layout: {
													sx: {
														'& .MuiMultiSectionDigitalClockSection-root': {
															'&::-webkit-scrollbar': {
																width: '4px',
															},
															'&::-webkit-scrollbar-track': {
																background: 'transparent',
															},
															'&::-webkit-scrollbar-thumb': {
																background: '#c0c0c0',
																borderRadius: '2px',
															},
															'&::-webkit-scrollbar-thumb:hover': {
																background: '#999',
															},
															scrollbarWidth: 'thin',
															scrollbarColor: '#c0c0c0 transparent',
														}
													}
												}
											}}
										/>
									</LocalizationProvider>
									{touched.startAt && errors.startAt && (
										<Box sx={{ color: '#d32f2f', fontSize: '0.75rem', mt: 0.5 }}>
											{errors.startAt}
										</Box>
									)}
								</Grid>

							{/* End Date */}
							<Grid item xs={12} sm={6}>
								<label className={styles.formLabel}>
									End Date <span className={styles.required}>*</span>
								</label>
								<TextField
									name="endDate"
									type="date"
									className={styles.formInput}
									required
									fullWidth
									value={getDateFromISO(values.endAt)}
									onChange={(e) =>
										handleDateTimeChange('endAt', e.target.value, getTimeFromISO(values.endAt))
									}
									inputProps={{
										min: getStartDateLimits.minDate,
									}}
									onBlur={handleBlur}
									error={touched.endAt && Boolean(errors.endAt)}
									helperText={touched.endAt && errors.endAt}
								/>
							</Grid>

							{/* End Time */}
							<Grid item xs={12} sm={6}>
								<label className={styles.formLabel}>
									End Time <span className={styles.required}>*</span>
								</label>
								<LocalizationProvider dateAdapter={AdapterDayjs}>
									<TimePicker
										value={dayjs(values.endAt).isValid() ? dayjs(values.endAt) : null}
										onChange={(newTime) => handleTimeChange('endAt', newTime)}
										views={['hours', 'minutes']}
										slotProps={{
											textField: {
												fullWidth: true,
												className: styles.formInput,
												error: touched.endAt && Boolean(errors.endAt),
											},
											layout: {
												sx: {
													'& .MuiMultiSectionDigitalClockSection-root': {
														'&::-webkit-scrollbar': {
															width: '4px',
														},
														'&::-webkit-scrollbar-track': {
															background: 'transparent',
														},
														'&::-webkit-scrollbar-thumb': {
															background: '#c0c0c0',
															borderRadius: '2px',
														},
														'&::-webkit-scrollbar-thumb:hover': {
															background: '#999',
														},
														scrollbarWidth: 'thin',
														scrollbarColor: '#c0c0c0 transparent',
													}
												}
											}
										}}
									/>
								</LocalizationProvider>
								{touched.endAt && errors.endAt && (
									<Box sx={{ color: '#d32f2f', fontSize: '0.75rem', mt: 0.5 }}>
										{errors.endAt}
									</Box>
								)}
							</Grid>
						</Grid>
					</form>
				</Box>
			</Box>
		</Paper>
	);
};

export default AddShiftPage;
